// Date helpers used across the Agenda sub-views. Local-based (no UTC drift).
export const DAY_MS = 86400000;

export function startOfWeek(d) {
  const day = (d.getDay() + 6) % 7; // Monday=0
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  s.setDate(s.getDate() - day);
  return s;
}

export function startOfMonth(d) {
  const s = new Date(d.getFullYear(), d.getMonth(), 1);
  s.setHours(0, 0, 0, 0);
  return s;
}

export function fmtDay(d) {
  return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" });
}

/** Local-based ISO date (YYYY-MM-DD). Never use toISOString() → shifts ±1 day in Europe. */
export function isoDay(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function isoWeek(d) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const y = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t - y) / 86400000 + 1) / 7);
}

/**
 * Resolve the visual color for an intervention with layered fallbacks:
 *   1. intervention.color (per-slot override)
 *   2. chantier.color (site-wide theme)
 *   3. technician.color (owner theme)
 *   4. #005CFF (BKflow default)
 */
export function ivColor(iv, chantiersMap, tech) {
  return iv?.color
    || (iv?.chantier_id ? chantiersMap?.[iv.chantier_id]?.color : null)
    || tech?.color
    || "#005CFF";
}

/**
 * Find the approved-leave entry that covers (userId, dayIso), or null.
 * `leaves` is the array returned by GET /leave-requests?status=approved&start&end.
 * Comparison is on YYYY-MM-DD strings which sort lexicographically like dates.
 */
export function leaveOnDay(userId, dayIso, leaves) {
  if (!userId || !dayIso || !leaves || !leaves.length) return null;
  return leaves.find((l) =>
    l.user_id === userId &&
    l.status === "approved" &&
    l.start_date <= dayIso &&
    l.end_date >= dayIso
  ) || null;
}

/** Local ISO end day for an intervention (defaults to its start day). */
export function ivEndDay(iv) {
  return (iv?.end_date || (iv?.scheduled_date || "").slice(0, 10)) || "";
}

/** True when `dayIso` falls within the intervention's span. */
export function ivCoversDay(iv, dayIso) {
  const s = (iv?.scheduled_date || "").slice(0, 10);
  const e = ivEndDay(iv);
  if (!s || !e || !dayIso) return false;
  return s <= dayIso && dayIso <= e;
}

/** Number of days this intervention spans (≥1). */
export function ivSpanDays(iv) {
  const s = (iv?.scheduled_date || "").slice(0, 10);
  const e = ivEndDay(iv);
  if (!s || !e) return 1;
  const sd = new Date(s + "T00:00:00");
  const ed = new Date(e + "T00:00:00");
  return Math.max(1, Math.round((ed - sd) / DAY_MS) + 1);
}

/** 1-based position of `dayIso` within the intervention's span. */
export function ivDayIndex(iv, dayIso) {
  const s = (iv?.scheduled_date || "").slice(0, 10);
  if (!s || !dayIso) return 1;
  const sd = new Date(s + "T00:00:00");
  const d = new Date(dayIso + "T00:00:00");
  return Math.max(1, Math.round((d - sd) / DAY_MS) + 1);
}

/** Human-readable label for the leave type shown in the agenda pill. */
export const LEAVE_LABEL = {
  paid: "Congé",
  rtt: "RTT",
  sick: "Maladie",
  unpaid: "Sans solde",
};

